import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, FileText, User, Eye } from 'lucide-react';
import { fetchCookbookById, Cookbook } from '@/lib/cookbooks';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './CookbookDetailPage.module.css';

export default function CookbookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasFullAccess } = useAuth();

  const { data: cookbook, isLoading, error } = useQuery({
    queryKey: ['cookbook', id],
    queryFn: () => fetchCookbookById(id!),
    enabled: !!id,
  });

  if (isLoading) return <LoadingSpinner />;

  if (error || !cookbook) {
    return (
      <div className={styles.errorState}>
        <BookOpen size={48} />
        <h2>Cookbook Not Found</h2>
        <p>We couldn't find this cookbook.</p>
        <Link to="/cookbooks" className={styles.backLink}>Return to Library</Link>
      </div>
    );
  }

  const canAccess = hasFullAccess || cookbook.isFreePreview;
  const showPreviewNote = !hasFullAccess && cookbook.hasPreview && !cookbook.isFreePreview;

  const formatDescription = (description: string) => {
    return description.split(/\n\n+/).map((paragraph, index) => (
      paragraph.trim() ? <p key={index} className={styles.descriptionParagraph}>{paragraph.trim()}</p> : null
    ));
  };

  return (
    <div className={styles.container}>
      <Link to="/cookbooks" className={styles.backButton}>
        <ArrowLeft size={20} />
        Back to Library
      </Link>

      <div className={styles.content}>
        <div className={styles.coverSection}>
          <img
            src={cookbook.coverImage}
            alt={cookbook.title}
            className={styles.coverImage}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-cookbook.png';
            }}
          />
          {!canAccess && <div className={styles.premiumBadge}>Premium</div>}
        </div>

        <div className={styles.detailsSection}>
          <h1 className={styles.title}>{cookbook.title}</h1>
          
          <div className={styles.meta}>
            <span className={styles.metaItem}>
              <User size={18} />
              {cookbook.author}
            </span>
            <span className={styles.metaItem}>
              <FileText size={18} />
              {cookbook.pageCount} pages
            </span>
          </div>

          <div className={styles.description}>
            {formatDescription(cookbook.description)}
          </div>

          {canAccess ? (
            <Link to={`/cookbooks/${cookbook.id}/view`} className={styles.viewButton}>
              <Eye size={20} />
              View Cookbook
            </Link>
          ) : showPreviewNote ? (
            <div className={styles.accessSection}>
              <Link to={`/cookbooks/${cookbook.id}/view`} className={styles.previewButton}>
                <Eye size={20} />
                View Preview
              </Link>
              <p className={styles.upgradeNote}>
                Upgrade to Premium for full access to this cookbook.
              </p>
            </div>
          ) : (
            <div className={styles.lockedSection}>
              <p className={styles.lockedText}>
                This cookbook is available for Premium members.
              </p>
              <p className={styles.upgradeNote}>
                Upgrade to access the full library of cookbooks.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
